import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { filterUserContextForCourse } from 'components/MarkCompleteSection/flag.on/helpers'
import {
  programs,
  useCourseManager,
} from 'layers/content/v2/hooks/useCourseManager'
import { useUserContext } from 'layers/content/v2/hooks/useUserContext'
import {
  operations as lmsOperations,
  selectors as lmsSelectors,
} from 'store/lmsManager'
import { findNode } from 'utils/highschoolHelpers'
import { getCurrentProgram, PROGRAM_TYPES } from 'utils/programHelpers'
import { isPreviewRoute } from 'utils/urlHelpers'
import Component from './component'

const INSTRUCTOR_KEY_TO_PROGRAM_KEY = {
  K5SelDigital: 'k5SelDigital',
  MspDigital: 'mspDigital',
  HighSchool: 'highSchool',
}

const resolveCourseManagerProgram = () => {
  const currentProgram = getCurrentProgram()
  switch (currentProgram) {
    case PROGRAM_TYPES.ELEMENTARY:
      return programs.elementary
    case PROGRAM_TYPES.MIDDLE_SCHOOL:
      return programs.middleSchool
    case PROGRAM_TYPES.HIGH_SCHOOL:
      return programs.highSchool
    default:
      return programs.none
  }
}

const getRegistrations = instructorData => {
  if (!instructorData) return []
  if (Array.isArray(instructorData)) return instructorData
  if (Array.isArray(instructorData.siteEducator))
    return instructorData.siteEducator
  if (Array.isArray(instructorData.educator)) return instructorData.educator
  if (typeof instructorData !== 'object') return []

  return Object.values(instructorData).flatMap(scope => {
    if (!scope) return []
    if (Array.isArray(scope)) return scope
    if (typeof scope !== 'object') return []
    if (typeof scope.registration_id !== 'undefined') return [scope]
    return Object.values(scope).flatMap(value => {
      if (!value) return []
      if (Array.isArray(value)) return value
      if (
        typeof value === 'object' &&
        typeof value.registration_id !== 'undefined'
      ) {
        return [value]
      }
      return []
    })
  })
}

const findLeafNodeIdFromLessonNodes = (lessonNodes, courseNodeId) => {
  if (!Array.isArray(lessonNodes) || !courseNodeId) return null
  const match = lessonNodes.find(node => {
    if (node?.course_node_id === courseNodeId) return true
    return typeof node?.leaf_node_id === 'string'
      ? node.leaf_node_id.startsWith(courseNodeId)
      : false
  })
  return match?.leaf_node_id || null
}

const findLeafNodeIdFromCourseTree = ({
  courseTrees,
  courseName,
  courseNodeId,
}) => {
  if (!courseTrees || !courseName || !courseNodeId) return null
  const courseTree = courseTrees[courseName]
  if (!courseTree) return null
  const node = findNode(courseTree, courseNodeId)
  return node?.leafNodeId || null
}

const normalizeCourseName = courseName => {
  if (!courseName) return null
  const trimmedCourseName = String(courseName).trim()
  if (!trimmedCourseName) return null
  if (/^k$/i.test(trimmedCourseName)) return 'K'
  if (/^(grade)?k$/i.test(trimmedCourseName)) return 'K'
  if (/^dwbgradek$/i.test(trimmedCourseName)) return 'K'
  if (/^kindergarten$/i.test(trimmedCourseName)) return 'K'
  const gradeMatch = trimmedCourseName.match(/^(?:dwb)?grade[_-]?([1-8])$/i)
  if (gradeMatch?.[1]) return gradeMatch[1]
  if (/^[1-8]$/.test(trimmedCourseName)) return trimmedCourseName
  if (/^(?:grade)?highschool$/i.test(trimmedCourseName)) return 'highschool'
  return trimmedCourseName
}

const getCourseNameFromPathname = pathname => {
  const match = String(pathname || '').match(/\/grade-([k1-8])\b/i)
  if (!match?.[1]) return null
  return match[1].toLowerCase() === 'k' ? 'K' : match[1]
}

const getCourseNameCandidates = ({ registrationCourseCode, pathname }) => {
  const candidates = []
  const addCandidate = candidate => {
    if (!candidate) return
    if (!candidates.includes(candidate)) {
      candidates.push(candidate)
    }
  }

  addCandidate(registrationCourseCode)
  addCandidate(normalizeCourseName(registrationCourseCode))
  addCandidate(getCourseNameFromPathname(pathname))
  return candidates
}

const checkIsComplete = ({
  registrations,
  registrationId,
  courseNodeId,
  leafNodeId,
}) => {
  if (!Array.isArray(registrations) || !courseNodeId) return false
  const matchedRegistration =
    registrationId != null
      ? registrations.find(
          registration =>
            String(registration.registration_id) === String(registrationId),
        )
      : null
  if (
    !matchedRegistration ||
    !Array.isArray(matchedRegistration.lesson_nodes)
  ) {
    return false
  }
  return matchedRegistration.lesson_nodes.some(lessonNode => {
    if (lessonNode.course_node_id === courseNodeId) return !!lessonNode.is_done
    if (leafNodeId && lessonNode.leaf_node_id === leafNodeId) {
      return !!lessonNode.is_done
    }
    return false
  })
}

const MarkCompleteV25Container = ({
  courseNodeId,
  instructorDataKey,
  useSecondaryMarkCompleteButton = false,
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const userContext = useUserContext()
  const resolvedCourseManagerProgram = useMemo(resolveCourseManagerProgram, [])
  const { courseTrees } = useCourseManager({
    program: resolvedCourseManagerProgram,
  })
  const isFetchingOrCreating = useSelector(
    state => !!state.lmsManager?.isFetchingOrCreatingProgramFlex,
  )
  const instructorData = useSelector(
    lmsSelectors.selectInstructorData(instructorDataKey),
  )

  const hasAttemptedInstructorFetch = useRef(false)

  useEffect(() => {
    if (isFetchingOrCreating || instructorData) return
    if (isPreviewRoute()) return
    if (hasAttemptedInstructorFetch.current) return
    hasAttemptedInstructorFetch.current = true
    dispatch(lmsOperations.fetchInstructorProgramFlexGet())
  }, [dispatch, isFetchingOrCreating, instructorData])

  const classId = useMemo(() => {
    const search =
      location?.search ||
      (typeof window !== 'undefined' ? window.location?.search || '' : '')
    const query = new URLSearchParams(search)
    return query.get('class')
  }, [location?.search])

  const registrations = useMemo(() => getRegistrations(instructorData), [
    instructorData,
  ])

  const selectedRegistration = useMemo(() => {
    if (!registrations.length) return null
    if (classId) {
      const classMatchedRegistration =
        registrations.find(
          registration =>
            String(registration.registration_id) === String(classId),
        ) || null
      if (classMatchedRegistration) return classMatchedRegistration
    }

    const gradeCourseNameFromPath = getCourseNameFromPathname(
      location?.pathname,
    )
    if (gradeCourseNameFromPath) {
      const routeMatchedRegistration = registrations.find(registration => {
        const normalizedCourseName = normalizeCourseName(
          registration?.course_name,
        )
        return normalizedCourseName === gradeCourseNameFromPath
      })
      if (routeMatchedRegistration) return routeMatchedRegistration
    }

    const lessonNodeMatchedRegistration = registrations.find(registration =>
      findLeafNodeIdFromLessonNodes(registration?.lesson_nodes, courseNodeId),
    )
    if (lessonNodeMatchedRegistration) return lessonNodeMatchedRegistration

    return registrations[0] || null
  }, [registrations, classId, location?.pathname, courseNodeId])

  const registrationId = selectedRegistration?.registration_id ?? null
  const courseNameCandidates = useMemo(
    () =>
      getCourseNameCandidates({
        registrationCourseCode: selectedRegistration?.course_name || null,
        pathname: location?.pathname,
      }),
    [selectedRegistration?.course_name, location?.pathname],
  )
  const registrationCourseCode =
    courseNameCandidates[0] || selectedRegistration?.course_name || null
  const resolvedCourseNameForTree = useMemo(
    () =>
      courseNameCandidates.find(courseName => !!courseTrees?.[courseName]) ||
      null,
    [courseNameCandidates, courseTrees],
  )
  const payloadCourseName =
    resolvedCourseNameForTree || normalizeCourseName(registrationCourseCode)

  const leafNodeId = useMemo(() => {
    if (!courseNodeId) return null
    const treeLeafNodeId = findLeafNodeIdFromCourseTree({
      courseTrees,
      courseName: resolvedCourseNameForTree || registrationCourseCode,
      courseNodeId,
    })
    if (treeLeafNodeId) return treeLeafNodeId
    return findLeafNodeIdFromLessonNodes(
      selectedRegistration?.lesson_nodes,
      courseNodeId,
    )
  }, [
    courseTrees,
    registrationCourseCode,
    resolvedCourseNameForTree,
    courseNodeId,
    selectedRegistration,
  ])

  const isComplete = useMemo(
    () =>
      checkIsComplete({
        registrations,
        registrationId,
        courseNodeId,
        leafNodeId,
      }),
    [registrations, registrationId, courseNodeId, leafNodeId],
  )

  const handleMarkComplete = async () => {
    if (isComplete) return
    if (!registrationId || !leafNodeId || !payloadCourseName) {
      console.error(
        '[MarkCompleteV2_5] registrationId, registrationCourseCode, and leafNodeId are required to mark complete',
      )
      return
    }

    const programKey = INSTRUCTOR_KEY_TO_PROGRAM_KEY[instructorDataKey]
    if (!programKey) {
      console.error(
        `[MarkCompleteV2_5] Unsupported instructorDataKey "${instructorDataKey}"`,
      )
      return
    }

    const filteredUserContext = filterUserContextForCourse(
      userContext,
      payloadCourseName,
      programKey,
    )

    await dispatch(
      lmsOperations.makeAttemptV2({
        registrationId,
        leafNodeId,
        progress: 1,
        userContextB64: btoa(JSON.stringify(filteredUserContext)),
      }),
    )
    await dispatch(lmsOperations.fetchInstructorProgramFlexGet())
  }

  return (
    <Component
      handleMarkComplete={handleMarkComplete}
      isComplete={isComplete}
      useSecondaryMarkCompleteButton={useSecondaryMarkCompleteButton}
    />
  )
}

MarkCompleteV25Container.propTypes = {
  courseNodeId: PropTypes.string,
  instructorDataKey: PropTypes.string.isRequired,
  useSecondaryMarkCompleteButton: PropTypes.bool,
}

export default MarkCompleteV25Container
